import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { advisorService, prospectService } from '../services/api';
import { AdvisorThreadSummary, Prospect } from '../types';
import { connectAdvisorSocket, onAdvisorThreadUpdatedEvent } from '../services/socket';

const AdvisorInbox: React.FC = () => {
  const navigate = useNavigate();
  const [threads, setThreads] = useState<AdvisorThreadSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState<'all' | 'unread'>('all');
  const [composeOpen, setComposeOpen] = useState(false);
  const [prospectQuery, setProspectQuery] = useState('');
  const [prospectResults, setProspectResults] = useState<Prospect[]>([]);
  const [prospectLoading, setProspectLoading] = useState(false);
  const [selectedProspect, setSelectedProspect] = useState<Prospect | null>(null);
  const [draftMessage, setDraftMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [composeError, setComposeError] = useState('');

  const loadThreads = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const data = await advisorService.listThreadsAdmin();
      setThreads(data);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to load advisor inbox');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadThreads();
  }, [loadThreads]);

  useEffect(() => {
    connectAdvisorSocket();
    let timer: number | null = null;
    const unsubscribe = onAdvisorThreadUpdatedEvent(() => {
      if (timer) window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        loadThreads();
      }, 150);
    });
    return () => {
      if (timer) window.clearTimeout(timer);
      unsubscribe();
    };
  }, [loadThreads]);

  useEffect(() => {
    const query = prospectQuery.trim();
    if (query.length < 2) {
      setProspectLoading(false);
      setProspectResults([]);
      return;
    }
    let active = true;
    setProspectLoading(true);
    setComposeError('');
    const timer = setTimeout(async () => {
      try {
        const response = await prospectService.getProspects(1, 15, undefined, query);
        if (!active) return;
        setProspectResults(response.prospects || []);
      } catch (err: any) {
        if (!active) return;
        setComposeError(err?.response?.data?.error || 'Failed to search prospects');
      } finally {
        if (active) setProspectLoading(false);
      }
    }, 250);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [prospectQuery]);

  const visibleThreads = useMemo(() => {
    const base = filter === 'unread'
      ? threads.filter((t) => (t.unread_count_admin || 0) > 0)
      : threads;
    return [...base].sort((a, b) => {
      const unreadA = a.unread_count_admin || 0;
      const unreadB = b.unread_count_admin || 0;
      if (unreadA !== unreadB) return unreadB - unreadA;
      const timeA = a.last_message_at ? new Date(a.last_message_at).getTime() : 0;
      const timeB = b.last_message_at ? new Date(b.last_message_at).getTime() : 0;
      return timeB - timeA;
    });
  }, [threads, filter]);

  const totalUnread = useMemo(
    () => threads.reduce((sum, t) => sum + (t.unread_count_admin || 0), 0),
    [threads]
  );

  const threadByProspectId = useMemo(() => {
    const map = new Map<number, AdvisorThreadSummary>();
    threads.forEach((thread) => {
      map.set(thread.prospect_id, thread);
    });
    return map;
  }, [threads]);

  const handleSelectProspect = (prospect: Prospect) => {
    setSelectedProspect(prospect);
    setComposeError('');
  };

  const resetCompose = () => {
    setComposeOpen(false);
    setProspectQuery('');
    setProspectResults([]);
    setSelectedProspect(null);
    setDraftMessage('');
    setComposeError('');
  };

  const handleSendMessage = async () => {
    if (!selectedProspect) {
      setComposeError('Select a prospect to message.');
      return;
    }
    if (!selectedProspect.user_id) {
      setComposeError('This prospect is not linked to a user account yet.');
      return;
    }
    const body = draftMessage.trim();
    if (!body) {
      setComposeError('Message body is required.');
      return;
    }
    try {
      setSending(true);
      setComposeError('');
      await advisorService.replyAdmin(selectedProspect.id, body);
      await loadThreads();
      navigate(`/admin/prospects/${selectedProspect.id}#advisor-messages`);
      resetCompose();
    } catch (err: any) {
      setComposeError(err?.response?.data?.error || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Advisor Inbox</h1>
          <p className="text-sm text-gray-600 dark:text-slate-300 mt-1">
            All advisor conversations in one place. Unread messages are highlighted.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="text-xs font-semibold uppercase tracking-wide text-amber-700 bg-amber-100 px-3 py-2 rounded-md">
            Unread: {totalUnread}
          </div>
          <button
            type="button"
            onClick={() => setComposeOpen((open) => !open)}
            className={`inline-flex items-center justify-center gap-2 font-semibold rounded-lg transition-colors px-4 py-2 text-sm ${
              composeOpen ? 'bg-white/80 dark:bg-slate-900/60 border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800' : 'bg-emerald-600 text-white border border-emerald-600 hover:bg-emerald-700 hover:border-emerald-700'
            }`}
          >
            {composeOpen ? 'Close' : 'New Message'}
          </button>
          <button
            type="button"
            onClick={loadThreads}
            className="inline-flex items-center justify-center gap-2 font-semibold rounded-lg transition-colors bg-white/80 dark:bg-slate-900/60 border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 px-4 py-2 text-sm"
          >
            Refresh
          </button>
        </div>
      </div>

      {composeOpen && (
        <div className="bg-white/85 dark:bg-slate-900/70 border border-slate-100/90 dark:border-slate-700/80 rounded-3xl shadow-[0_14px_36px_rgba(15,23,42,0.12)] dark:shadow-[0_18px_40px_rgba(0,0,0,0.55)] backdrop-blur-[18px] overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-100/80 dark:border-slate-700/80 flex items-center justify-between">
            <h2 className="text-lg font-medium text-slate-900 dark:text-slate-50">Compose message</h2>
          </div>
          <div className="p-4 sm:p-6 space-y-4">
            <div className="flex flex-col lg:flex-row gap-4 lg:items-end">
            <div className="flex-1 space-y-2">
              <label className="block text-sm font-semibold text-gray-800">
                Find a prospect
              </label>
              <input
                type="text"
                value={prospectQuery}
                onChange={(e) => setProspectQuery(e.target.value)}
                placeholder="Search by name or email"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-amber-400"
              />
              <div className="text-xs text-gray-500">
                Type at least 2 characters to search.
              </div>
            </div>
            {selectedProspect && (
              <div className="lg:min-w-[260px] rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
                <div className="text-xs font-semibold uppercase tracking-wide text-amber-700">
                  Selected
                </div>
                <div className="text-sm font-semibold text-amber-950 mt-1">
                  {selectedProspect.first_name} {selectedProspect.last_name}
                </div>
                <div className="text-xs text-amber-800">
                  {selectedProspect.email || 'No email on file'}
                </div>
                {!selectedProspect.user_id && (
                  <div className="text-[11px] font-semibold text-red-700 mt-1">
                    No user account linked yet
                  </div>
                )}
              </div>
            )}
          </div>

            {composeError && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
                {composeError}
              </div>
            )}

            {prospectLoading && (
              <div className="text-sm text-gray-500">Searching prospects...</div>
            )}

            {!prospectLoading && prospectResults.length > 0 && (
              <div className="border border-gray-100 rounded-xl overflow-hidden">
              <div className="divide-y divide-gray-100 dark:divide-slate-700/60 max-h-64 overflow-auto">
                  {prospectResults.map((prospect) => {
                    const linkedThread = threadByProspectId.get(prospect.id);
                    const isSelected = selectedProspect?.id === prospect.id;
                    return (
                      <button
                        key={prospect.id}
                        type="button"
                        onClick={() => handleSelectProspect(prospect)}
                        className={`w-full text-left px-4 py-3 transition ${
                          isSelected ? 'bg-amber-50' : 'bg-white hover:bg-gray-50'
                        }`}
                      >
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
                          <div>
                            <div className="text-sm font-semibold text-gray-900">
                              {prospect.first_name} {prospect.last_name}
                            </div>
                            <div className="text-xs text-gray-500">
                              {prospect.email || 'No email on file'}
                            </div>
                          </div>
                          <div className="flex items-center gap-2 text-xs">
                            {!prospect.user_id && (
                              <span className="px-2 py-1 rounded-full bg-red-50 text-red-700 border border-red-100 font-semibold">
                                No account
                              </span>
                            )}
                            {linkedThread && (
                              <span className="px-2 py-1 rounded-full bg-gray-900 text-white font-semibold">
                                Existing thread
                              </span>
                            )}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {selectedProspect && (
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-800">
                  Message
                </label>
                <textarea
                  value={draftMessage}
                  onChange={(e) => setDraftMessage(e.target.value)}
                  placeholder={`Write a message to ${selectedProspect.first_name || 'the prospect'}...`}
                  rows={4}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-amber-400"
                />
                <div className="flex flex-wrap items-center gap-2">
                  {threadByProspectId.get(selectedProspect.id) && (
                    <button
                      type="button"
                      onClick={() => navigate(`/admin/prospects/${selectedProspect.id}#advisor-messages`)}
                      className="inline-flex items-center justify-center gap-2 font-semibold rounded-lg transition-colors bg-white/80 dark:bg-slate-900/60 border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 px-4 py-2 text-sm"
                    >
                      Open Thread
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={handleSendMessage}
                    disabled={sending}
                    className="inline-flex items-center justify-center gap-2 font-semibold rounded-lg transition-colors bg-emerald-600 text-white border border-emerald-600 hover:bg-emerald-700 hover:border-emerald-700 px-4 py-2 text-sm disabled:opacity-60"
                  >
                    {sending ? 'Sending...' : 'Send Message'}
                  </button>
                  <button
                    type="button"
                    onClick={resetCompose}
                    className="inline-flex items-center justify-center gap-2 font-semibold rounded-lg transition-colors bg-white/80 dark:bg-slate-900/60 border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 px-4 py-2 text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => setFilter('all')}
          className={`inline-flex items-center justify-center gap-2 font-semibold rounded-lg transition-colors px-4 py-2 text-sm ${
            filter === 'all' ? 'bg-blue-600 text-white border border-blue-600 hover:bg-blue-700 hover:border-blue-700' : 'bg-white/80 dark:bg-slate-900/60 border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800'
          }`}
        >
          All Threads
        </button>
        <button
          type="button"
          onClick={() => setFilter('unread')}
          className={`inline-flex items-center justify-center gap-2 font-semibold rounded-lg transition-colors px-4 py-2 text-sm ${
            filter === 'unread' ? 'bg-amber-500 text-amber-950 border border-amber-500 hover:bg-amber-600 hover:border-amber-600' : 'bg-white/80 dark:bg-slate-900/60 border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800'
          }`}
        >
          Unread Only
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
          {error}
        </div>
      )}

      <div className="bg-white/85 dark:bg-slate-900/70 border border-slate-100/90 dark:border-slate-700/80 rounded-3xl shadow-[0_14px_36px_rgba(15,23,42,0.12)] dark:shadow-[0_18px_40px_rgba(0,0,0,0.55)] backdrop-blur-[18px] overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100/80 dark:border-slate-700/80 flex items-center justify-between">
          <h2 className="text-lg font-medium text-slate-900 dark:text-slate-50">Threads</h2>
          <p className="text-xs text-gray-500">{visibleThreads.length} visible</p>
        </div>
        {loading ? (
          <div className="px-6 py-10 text-sm text-gray-500">Loading advisor inbox...</div>
        ) : visibleThreads.length === 0 ? (
          <div className="px-6 py-12 text-center">
            <p className="text-lg font-semibold text-gray-900">No conversations yet</p>
            <p className="text-sm text-gray-500 mt-1">When students message advisors, they will appear here.</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100 dark:divide-slate-700/60">
            {visibleThreads.map((thread) => {
              const unread = thread.unread_count_admin || 0;
              const hasUnread = unread > 0;
              return (
                <button
                  key={thread.thread_id}
                  type="button"
                  onClick={() => navigate(`/admin/prospects/${thread.prospect_id}#advisor-messages`)}
                  className={`w-full text-left px-6 py-5 transition hover:bg-gray-50 dark:hover:bg-slate-800/60 ${
                    hasUnread ? 'bg-amber-50/60 dark:bg-amber-500/10' : 'bg-white dark:bg-slate-900/40'
                  }`}
                >
                  <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <span className="text-base font-semibold text-gray-900">
                          {thread.prospect_name || `Prospect #${thread.prospect_id}`}
                        </span>
                        {hasUnread && (
                          <span className="min-w-[22px] h-6 px-2 rounded-full bg-amber-400 text-amber-900 text-xs font-bold flex items-center justify-center">
                            {unread}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500">
                        {thread.prospect_email || 'No email on file'}
                      </div>
                      <div className="text-sm text-gray-700 line-clamp-2">
                        {thread.last_message_preview || 'No messages yet.'}
                      </div>
                    </div>
                    <div className="text-xs text-gray-500 flex flex-col items-start lg:items-end gap-1">
                      <span>
                        {thread.last_message_at
                          ? new Date(thread.last_message_at).toLocaleString()
                          : 'No activity yet'}
                      </span>
                      <span className="font-semibold text-gray-700">
                        Messages: {thread.message_count}
                      </span>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdvisorInbox;
